import { useCallback, useEffect, useRef, useState } from 'react'

import { useLocalStorage } from '@uidotdev/usehooks'

import { checkInFromQrResolution } from '@/features/partners/lib/checkInFromQrResolution'
import { notifyPartnerCheckIn } from '@/features/partners/lib/notifyPartnerCheckIn'
import { useDigitalProductContext } from '@/shared/contexts/DigitalProductContext'
import { useAppRouter as useRouter } from '@/shared/hooks/useAppRouter'
import { useNavigation } from '@/shared/hooks/useNavigation'
import { useUserProgress } from '@/shared/hooks/useUserProgress'
import { showErrorAlert } from '@/shared/lib/errors'
import {
  resolveQrCode,
  getQrNavigationPath,
  navigateFromQrResolution,
  isBonusResolution,
  getUnavailableAlert,
  QR_READING_DIALOG_SOURCE
} from '@/shared/lib/qr-resolver'
import { alert, clearDialogs, confirm } from '@/shared/ui/dialog'
import { TopProgressBar } from '@/shared/ui/TopProgressBar'
import { useReloadGuard } from '@/shared/version'

const NOT_FOUND_ALERT = {
  variant: 'info',
  title: 'QR code não reconhecido',
  description:
    'Não encontramos um destino para este código. Confira se ele ainda é válido ou tente novamente.',
  source: QR_READING_DIALOG_SOURCE
}

// Só o que um navegador abre com segurança. A api já recusa outro esquema na
// gravação, mas esta é a linha que de fato executa o valor: uma única linha
// gravada por fora da validação (import, seed, `update_column`) viraria XSS
// armazenado no app do participante. A guarda mora nos dois lugares de
// propósito.
const NAVIGABLE_SCHEMES = ['http:', 'https:']

const isNavigable = destination => {
  try {
    return NAVIGABLE_SCHEMES.includes(new URL(destination).protocol)
  } catch {
    return false
  }
}

const hostOf = destination => {
  try {
    return new URL(destination).host
  } catch {
    return destination
  }
}

const internalPathOf = destination => {
  try {
    const url = new URL(destination)

    return `${url.pathname}${url.search}`
  } catch {
    return destination
  }
}

export const QRCodesWatcher = () => {
  const router = useRouter()
  const { basePathWithId, navigateTo } = useNavigation()
  const { eventSlug } = useDigitalProductContext()
  const { partnerWasFinished } = useUserProgress()
  const { qr_code: queryQrCode } = router.query
  const [qrCode, setQrCode] = useLocalStorage('evnttz.qr_code', null)

  // Um código é processado **uma vez só**, por leitura.
  //
  // `loadQrCode` tem `await` no meio (resolução e, para Parceiro, o registro do
  // check-in). Entre eles, `setQrCode(null)` e `clearQrCodeParam()` disparam
  // re-render, e o efeito lá embaixo reescreve `qrCode` a partir do
  // `?qr_code=` que ainda não saiu da URL — a leitura recomeça. Medido no smoke
  // de 2026-09-12: três registros e três confirmações para uma leitura só.
  //
  // A guarda é por valor do código, não booleana: ler outro QR em seguida tem
  // de funcionar sem recarregar a página. Quem a libera é a chegada de um
  // `?qr_code=` novo — ver o efeito de consumo do parâmetro.
  const processedQrCodeRef = useRef(null)

  // Nada na tela indicava a espera: este componente renderiza fora das rotas e
  // a leitura passou a aguardar a resposta mais pesada do app
  // (`Progress::ShowSerializer`). A tela anterior ficava parada por segundos,
  // que na fila do estande se parece com app travado.
  const [isReading, setIsReading] = useState(false)

  // Aqui não há `status` como no `Watcher`: o sinal de leitura em andamento é
  // haver um código guardado esperando resolução. Recarregar nesse intervalo
  // perderia o QR que o participante acabou de ler.
  useReloadGuard(Boolean(qrCode), 'qr-code')

  const loadQrCode = useCallback(async () => {
    if (!qrCode) return
    if (processedQrCodeRef.current === qrCode) return

    processedQrCodeRef.current = qrCode
    setIsReading(true)

    // Leitura nova é intenção nova: o diálogo da leitura anterior não pode
    // aparecer no lugar do desta. Só o que esta leitura enfileirou — descartar
    // a fila inteira responderia por pergunta de fluxo alheio.
    clearDialogs(QR_READING_DIALOG_SOURCE)

    try {
      const resolution = await resolveQrCode(qrCode)

      switch (resolution.status) {
        case 'link':
          setQrCode(null)
          clearQrCodeParam()
          // Daqui para a frente a espera é pela decisão do participante, não
          // pela rede: a barra sairia mentindo sobre quem está devendo.
          setIsReading(false)
          await followLink(resolution)
          break
        case 'resolved': {
          setQrCode(null)
          clearQrCodeParam()

          // Lido ANTES do registro: depois dele o progresso já contém o id e
          // os dois casos ficam indistinguíveis.
          const alreadyDone =
            resolution.resourceType === 'Partner' &&
            typeof resolution.resource.id === 'number' &&
            partnerWasFinished(resolution.resource.id)

          const checkIn = await checkInFromQrResolution(resolution, eventSlug)

          navigateFromQrResolution({
            resolution,
            pathname: router.pathname,
            router,
            navigateTo
          })

          // Depois de navegar: o diálogo do `DialogHost` sobrevive à troca de
          // rota e aparece já sobre o perfil do parceiro, que é onde a
          // confirmação faz sentido.
          notifyPartnerCheckIn(checkIn, resolution, alreadyDone)
          break
        }
        case 'unavailable':
          setQrCode(null)
          clearQrCodeParam()
          if (!isBonusResolution(resolution.resourceType)) {
            navigateTo(getQrNavigationPath(resolution))
          }
          void alert({
            ...getUnavailableAlert(resolution),
            source: QR_READING_DIALOG_SOURCE
          })
          break
        case 'not_found':
          cleanup()
          void alert(NOT_FOUND_ALERT)
          break
        case 'technical_error':
          cleanup()
          showErrorAlert(resolution.error)
          break
      }
    } finally {
      setIsReading(false)
    }
  }, [qrCode, basePathWithId, navigateTo, router, eventSlug, partnerWasFinished])

  // Destino externo nunca sai sozinho: quem leu um QR impresso não escolheu
  // deixar o evento, e sair sem aviso é o que transforma um código da casa em
  // vetor de phishing. O aviso mostra o host — é o que cabe na tela e é o dado
  // que a pessoa usa para decidir se confia.
  //
  // Destino interno é navegação normal do app: vai pelo router, para não
  // recarregar a SPA inteira.
  const followLink = async ({ destination, external }) => {
    if (!isNavigable(destination)) {
      void alert(NOT_FOUND_ALERT)
      return
    }

    if (!external) return navigateTo(internalPathOf(destination))

    const confirmed = await confirm({
      title: 'Você está saindo do evento',
      description: `Este código leva para ${hostOf(destination)}, um endereço fora do evento. Continue apenas se reconhecer o destino.`,
      confirmLabel: 'Continuar',
      cancelLabel: 'Ficar no evento',
      variant: 'warning',
      source: QR_READING_DIALOG_SOURCE
    })

    if (confirmed) window.location.href = destination
  }

  const cleanup = () => {
    router.replace(basePathWithId || '/')
    setQrCode(null)
  }

  // O código já foi consumido quando a resolução volta: deixá-lo na URL faz o
  // efeito abaixo reintroduzi-lo em `qrCode` a cada render, o que reabre o
  // alerta e — com a métrica ligada — conta uma leitura a mais por recarga.
  //
  // Só o parâmetro sai; o caminho e os demais parâmetros ficam, porque a
  // navegação de destino é responsabilidade de quem trata cada desfecho.
  // Os desfechos de erro não passam por aqui: `cleanup` já troca a URL inteira.
  const clearQrCodeParam = () => {
    const { qr_code: _consumed, ...rest } = router.query
    const search = new URLSearchParams(rest).toString()

    router.replace(search ? `${router.pathname}?${search}` : router.pathname)
  }

  // O `?qr_code=` é uma **chegada**, não um estado. Reagir a ele enquanto ele
  // estiver na URL é o que ressuscitava o código já consumido: `clearQrCodeParam`
  // navega pelo TanStack Router, que publica a URL nova num tick posterior, e
  // no intervalo há renders com o parâmetro velho ainda na query.
  //
  // Antes da guarda de re-entrância isso custava três registros por leitura.
  // Com ela, custava pior: o `return` cedo de `loadQrCode` fazia o código
  // voltar a `qrCode` e ficar lá — `evnttz.qr_code` no localStorage e o
  // `useReloadGuard` acima armado pelo resto da sessão, com o código sendo
  // reprocessado no próximo carregamento da página.
  //
  // Consumir o parâmetro uma vez por chegada resolve os dois. E é aqui, não
  // nos ramos de erro, que `processedQrCodeRef` é liberado: chegada nova é
  // intenção nova mesmo quando o código é o mesmo (releitura do mesmo QR, ou
  // nova tentativa depois de `not_found`/`technical_error`). Liberar dentro do
  // ramo de erro reabriria o loop, porque lá o parâmetro velho ainda está na
  // URL.
  const consumedQueryQrCodeRef = useRef(null)

  useEffect(() => {
    if (queryQrCode === consumedQueryQrCodeRef.current) return

    consumedQueryQrCodeRef.current = queryQrCode ?? null

    if (!queryQrCode) return

    processedQrCodeRef.current = null
    setQrCode(queryQrCode)
  }, [queryQrCode])

  useEffect(() => {
    loadQrCode()
  }, [loadQrCode])

  return isReading ? <TopProgressBar /> : null
}
